import { useCallback, useEffect, useMemo, useState } from "react";

import { Label, Typography } from "@king-ict/eupisi-ui/components";
import { hrDate } from "@king-ict/eupisi-ui/utils";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { FormCard } from "./FormCard";
import styles from "./RequestHeader.module.scss";
import {
  getCommitteeDecisionList,
  getEnrollmentDecision,
} from "../../api/Request/RequestApi";
import { generateSecondaryInstitutionApprovedText } from "../../features/Request/utils/strings";
import { useEnumCodeBooks } from "../../hooks";
import { InstitutionCodeBook } from "../../models";
import {
  Request,
  CommitteeDecision,
  OutsideEnrollmentAreaStatus,
  EnrollmentDecision,
} from "../../models/Request";
import { RequestStatus } from "../../models/Request/RequestStatus";
import { useGetInstitutionListQuery } from "../../queries/codeBookQuery";
import { FormatUtils, isStatusSuggestedEnrollment } from "../../utils";

export interface IProps {
  request: Request;
}

export const RequestHeader = ({ request }: IProps) => {
  const { t } = useTranslation();

  const { requestTypeCodeBook, requestStatusCodeBook } = useEnumCodeBooks();

  const { data: institutions } = useGetInstitutionListQuery();
  const [primaryInstitution, setPrimaryInstitution] =
    useState<InstitutionCodeBook>();
  const [committeeDecisions, setCommitteeDecisions] = useState<
    CommitteeDecision[]
  >([]);
  const [enrollmentDecision, setEnrollmentDecision] =
    useState<EnrollmentDecision>();

  useEffect(() => {
    if (!request?.institutionId || !institutions) return;

    setPrimaryInstitution(
      institutions?.find((i) => i.id === request.institutionId)
    );
  }, [request.institutionId, institutions]);
  useEffect(() => {
    (async () => {
      if (request.institutionCommitteeId) {
        const committeeDecisions = await getCommitteeDecisionList(request.id);
        if (committeeDecisions) {
          const institutionCommitteeDecisions = committeeDecisions.filter(
            (v) =>
              v.committeeEmployee?.committeeId ===
              request.institutionCommitteeId
          );
          setCommitteeDecisions(institutionCommitteeDecisions);
        }
      }

      if (request.id !== 0) {
        const enrollmentDecision = await getEnrollmentDecision(request.id);
        setEnrollmentDecision(enrollmentDecision);
      }
    })();
  }, [request.id, request.institutionCommitteeId]);

  const renderEnrollmentAreaStatus = useCallback(() => {
    if (
      !request.outsideEnrollmentAreaStatus ||
      request.outsideEnrollmentAreaStatus ===
        OutsideEnrollmentAreaStatus.NotProcessed
    ) {
      return (
        <Typography>
          {t("common.requestModel.status")}:{" "}
          {t("common.requestModel.notDecided")}
        </Typography>
      );
    }

    if (isStatusSuggestedEnrollment(request.outsideEnrollmentAreaStatus)) {
      return (
        <Typography>
          {t("common.requestModel.status")}:{" "}
          {generateSecondaryInstitutionApprovedText(
            request,
            enrollmentDecision
          )}
        </Typography>
      );
    }

    if (
      request.outsideEnrollmentAreaStatus ===
      OutsideEnrollmentAreaStatus.Decision
    ) {
      const getInstitutionName = () => {
        if (enrollmentDecision?.solutionId !== undefined) {
          return t(
            "suggestedOutsideEnrollmentArea.approvedByEnrollmentDecision"
          );
        }

        const institutionId = request.approvedEnrollmentOutsideEnrollmentArea
          ? request.outsideEnrollmentAreaInstitutionProgram?.institutionId
          : request.institutionId;

        return institutions?.find((v) => v.id === institutionId)?.name ?? "";
      };

      return (
        <Typography>
          {t("common.requestModel.status")}:{" "}
          {t("outsideEnrollmentAreaStatus.Decision")}, {getInstitutionName()}
        </Typography>
      );
    }

    return null;
  }, [enrollmentDecision, institutions, request, t]);

  const outsideEnrollmentAreaInstitution = useMemo(
    () =>
      institutions?.find(
        (v) =>
          v.id ===
          request.outsideEnrollmentAreaInstitutionProgram?.institutionId
      ),
    [
      institutions,
      request.outsideEnrollmentAreaInstitutionProgram?.institutionId,
    ]
  );

  if (!request) return null;

  const getRequestStatusDisplayName = (
    request: Request,
    requestStatusCodeBook: { id: number; name: string }[]
  ): string => {
    if (
      request.statusId === RequestStatus.RegistrationFormCreated &&
      typeof request.requestEnrollmentStatus === "object"
    ) {
      return (
        request.requestEnrollmentStatus.publicName ??
        requestStatusCodeBook.find((v) => v.id === request.statusId)?.name ??
        "-"
      );
    }

    return (
      requestStatusCodeBook.find((v) => v.id === request.statusId)?.name ?? "-"
    );
  };

  return (
    <FormCard className={styles.formCard}>
      <Col className={styles.singleColumn}>
        <Typography>
          {request.childDetails?.firstName} {request.childDetails?.lastName},
          OIB: {request.childDetails?.person?.oib}
        </Typography>
        <Typography>
          {t("common.schoolCandidateModel.dateOfBirth")}:{" "}
          {request.childDetails?.dateOfBirth
            ? hrDate(new Date(request.childDetails?.dateOfBirth))
            : ""}
        </Typography>
        <Typography>
          {t("common.schoolCandidateModel.permanentAddress")}:{" "}
          {FormatUtils.getAddressString(
            request.childDetails?.residence?.permanentAddress,
            request.childDetails?.residence?.address
          )}
        </Typography>
        <Typography>
          {t("common.schoolCandidateModel.temporaryAddress")}:{" "}
          {FormatUtils.getAddressString(
            request.childDetails?.residence?.temporaryAddress
          )}
        </Typography>
        <Row className={styles.spacedRow}>
          <Typography className={styles.valueTypography}>
            {t("common.requestModel.requestType")}:
          </Typography>
          <Label
            color={FormatUtils.getRequestTypeColor(request.type)}
            className={styles.label}
            outline={false}
            fullWidth={false}
          >
            {requestTypeCodeBook.find((v) => v.id === request.type)?.name}
          </Label>
        </Row>
        <Row className={styles.spacedRow}>
          <Typography className={styles.valueTypography}>
            {t("common.status")}:
          </Typography>
          <Label
            color={FormatUtils.getRequestStatusColor(request.statusId)}
            className={styles.label}
            outline={false}
            fullWidth={false}
          >
            {getRequestStatusDisplayName(request, requestStatusCodeBook)}
          </Label>
        </Row>
      </Col>

      <Col className={styles.singleColumn}>
        <Typography>
          {t(
            "common.requestModel.requestTabs.institutionCommitteeDecisionInfo"
          )}
          : {!!committeeDecisions.length ? t("common.yes") : t("common.no")}
        </Typography>
        {!!primaryInstitution?.name && (
          <Typography>
            {t("request.primaryInstitution")}: {primaryInstitution.name}
          </Typography>
        )}
        <Typography>
          {t("request.hasSecondaryInstitution")}:{" "}
          {request.isOutsideEnrollmentArea
            ? `${t("common.yes")}${
                outsideEnrollmentAreaInstitution?.name
                  ? `, ${outsideEnrollmentAreaInstitution.name}`
                  : ""
              }`
            : t("common.no")}
        </Typography>
        {request.isOutsideEnrollmentArea && renderEnrollmentAreaStatus()}
        {request.enrollmentInstitutionId && (
          <Typography>
            {t("common.requestModel.enrollmentInstitution")}:{" "}
            {
              institutions?.find(
                (v) => v.id === request.enrollmentInstitutionId
              )?.name
            }
          </Typography>
        )}
      </Col>
    </FormCard>
  );
};
